import { computed, inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

export interface ShuttleRoutePoint {
  lat: number;
  lng: number;
}

export interface ShuttleActiveOrderRecord {
  lineId: string;
  shuttleId: string | null;
  partnerId: string | null;
  statuses: number[];
  bookingProcessingTime: string | null;
  dispatchTime: string | null;
  shuttleStatusId: number | null;
  orderIds: string[];
  optionIds: number[];
  userIds: string[];
}

export interface ShuttleLineRecord {
  id: string;
  name: string;
  regionId: number | null;
  tariffId: number | null;
  serviceTypeId: number | null;
  serviceType: string | null;
  startAddress: string;
  endAddress: string;
  startPoint: ShuttleRoutePoint | null;
  endPoint: ShuttleRoutePoint | null;
  dispatchTime: string | null;
  route: ShuttleRoutePoint[];
}

export interface ShuttleRecord {
  id: string;
  lineId: string | null;
  status: string | null;
  shuttleStatusId: number | null;
  serviceTypeId: number | null;
  reservations: number;
  seats: number;
  partnerId: string | null;
  bookingProcessingTime: string | null;
  dispatchTime: string | null;
  access: string[];
  unitFare: number | null;
}

export interface ShuttlePartnerRecord {
  shuttleId: string;
  userId: string;
  mobile: string | null;
  name: string;
  car: string | null;
  number: string | null;
  color: string | null;
  seatCount: number | null;
  steeringWheel: string | null;
}

export interface ShuttleClientRecord {
  shuttleId: string;
  userId: string;
  mobile: string | null;
  orderId: string | null;
  orderStatus: string | null;
  code: string | null;
  seatsReserved: number;
  fare: number;
  isPaid: boolean;
  codeConfirmed: string | null;
  optionIds: number[];
  paymentMethodId: number | null;
}

export interface ShuttleNoteRecord {
  id: string;
  noteId: number | null;
  shuttleId: string;
  creator: string | null;
  text: string;
  createdAt: string | null;
  deletedAt: string | null;
}

export interface ShuttleStateModel {
  activeOrders: ShuttleActiveOrderRecord[];
  linesById: Record<string, ShuttleLineRecord>;
  lineIds: string[];
  shuttlesById: Record<string, ShuttleRecord>;
  shuttleIdsByLineId: Record<string, string[]>;
  partnersByShuttleId: Record<string, ShuttlePartnerRecord[]>;
  clientsByShuttleId: Record<string, ShuttleClientRecord[]>;
  notesByShuttleId: Record<string, ShuttleNoteRecord[]>;
  selectedLineId: string | null;
  selectedShuttleId: string | null;
  updatedAt: string | null;
}

const SHUTTLE_STATE_DEFAULTS: ShuttleStateModel = {
  activeOrders: [],
  linesById: {},
  lineIds: [],
  shuttlesById: {},
  shuttleIdsByLineId: {},
  partnersByShuttleId: {},
  clientsByShuttleId: {},
  notesByShuttleId: {},
  selectedLineId: null,
  selectedShuttleId: null,
  updatedAt: null
};

export class ReplaceShuttleSnapshot {
  static readonly type = '[Shuttle] Replace Snapshot';

  constructor(public readonly payload: Partial<ShuttleStateModel>) {}
}

export class SelectShuttleLine {
  static readonly type = '[Shuttle] Select Line';

  constructor(public readonly lineId: string | null) {}
}

export class SelectShuttle {
  static readonly type = '[Shuttle] Select Shuttle';

  constructor(public readonly shuttleId: string | null) {}
}

export class ClearShuttleState {
  static readonly type = '[Shuttle] Clear';
}

@State<ShuttleStateModel>({
  name: 'shuttle',
  defaults: SHUTTLE_STATE_DEFAULTS
})
@Injectable()
export class ShuttleState {
  @Selector()
  static snapshot(state: ShuttleStateModel): ShuttleStateModel {
    return state;
  }

  @Selector()
  static selectedLineId(state: ShuttleStateModel): string | null {
    return state.selectedLineId;
  }

  @Selector()
  static selectedShuttleId(state: ShuttleStateModel): string | null {
    return state.selectedShuttleId;
  }

  @Action(ReplaceShuttleSnapshot)
  replaceSnapshot(ctx: StateContext<ShuttleStateModel>, action: ReplaceShuttleSnapshot): void {
    ctx.patchState({
      ...action.payload,
      updatedAt: action.payload.updatedAt ?? new Date().toISOString()
    });
  }

  @Action(SelectShuttleLine)
  selectShuttleLine(ctx: StateContext<ShuttleStateModel>, action: SelectShuttleLine): void {
    ctx.patchState({
      selectedLineId: action.lineId
    });
  }

  @Action(SelectShuttle)
  selectShuttle(ctx: StateContext<ShuttleStateModel>, action: SelectShuttle): void {
    ctx.patchState({
      selectedShuttleId: action.shuttleId
    });
  }

  @Action(ClearShuttleState)
  clearShuttleState(ctx: StateContext<ShuttleStateModel>): void {
    ctx.setState(SHUTTLE_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class ShuttleFacade {
  private readonly store = inject(Store);

  readonly shuttle = this.store.selectSignal(ShuttleState.snapshot);
  readonly selectedLineId = this.store.selectSignal(ShuttleState.selectedLineId);
  readonly selectedShuttleId = this.store.selectSignal(ShuttleState.selectedShuttleId);
  readonly activeOrders = computed<ShuttleActiveOrderRecord[]>(() => this.shuttle().activeOrders);
  readonly lineList = computed<ShuttleLineRecord[]>(() => {
    const state = this.shuttle();

    return state.lineIds
      .map((lineId) => state.linesById[lineId])
      .filter((line): line is ShuttleLineRecord => Boolean(line));
  });
  readonly selectedLine = computed<ShuttleLineRecord | null>(() => {
    const selectedLineId = this.selectedLineId();
    const state = this.shuttle();

    if (!selectedLineId) {
      return null;
    }

    return state.linesById[selectedLineId] ?? null;
  });
  readonly selectedLineShuttles = computed<ShuttleRecord[]>(() => {
    const selectedLineId = this.selectedLineId();
    const state = this.shuttle();

    if (!selectedLineId) {
      return [];
    }

    return (state.shuttleIdsByLineId[selectedLineId] ?? [])
      .map((shuttleId) => state.shuttlesById[shuttleId])
      .filter((shuttle): shuttle is ShuttleRecord => Boolean(shuttle));
  });
  readonly selectedShuttle = computed<ShuttleRecord | null>(() => {
    const selectedShuttleId = this.selectedShuttleId();
    const state = this.shuttle();

    if (!selectedShuttleId) {
      return null;
    }

    return state.shuttlesById[selectedShuttleId] ?? null;
  });
  readonly selectedShuttlePartners = computed<ShuttlePartnerRecord[]>(() => {
    const selectedShuttleId = this.selectedShuttleId();
    const state = this.shuttle();

    if (!selectedShuttleId) {
      return [];
    }

    return state.partnersByShuttleId[selectedShuttleId] ?? [];
  });
  readonly selectedShuttleClients = computed<ShuttleClientRecord[]>(() => {
    const selectedShuttleId = this.selectedShuttleId();
    const state = this.shuttle();

    if (!selectedShuttleId) {
      return [];
    }

    return state.clientsByShuttleId[selectedShuttleId] ?? [];
  });
  readonly selectedShuttleNotes = computed<ShuttleNoteRecord[]>(() => {
    const selectedShuttleId = this.selectedShuttleId();
    const state = this.shuttle();

    if (!selectedShuttleId) {
      return [];
    }

    return state.notesByShuttleId[selectedShuttleId] ?? [];
  });

  replaceSnapshot(payload: Partial<ShuttleStateModel>): void {
    this.store.dispatch(new ReplaceShuttleSnapshot(payload));
  }

  setActiveOrders(activeOrders: ShuttleActiveOrderRecord[]): void {
    this.replaceSnapshot({
      activeOrders
    });
  }

  setLines(lines: ShuttleLineRecord[]): void {
    this.replaceSnapshot({
      linesById: indexBy(lines, (line) => line.id),
      lineIds: lines.map((line) => line.id),
      shuttlesById: {},
      shuttleIdsByLineId: {},
      partnersByShuttleId: {},
      clientsByShuttleId: {},
      notesByShuttleId: {}
    });
  }

  setLineShuttles(lineId: string, shuttles: ShuttleRecord[]): void {
    const state = this.shuttle();
    const nextShuttlesById = { ...state.shuttlesById };

    for (const shuttle of shuttles) {
      nextShuttlesById[shuttle.id] = shuttle;
    }

    this.replaceSnapshot({
      shuttlesById: nextShuttlesById,
      shuttleIdsByLineId: {
        ...state.shuttleIdsByLineId,
        [lineId]: shuttles.map((shuttle) => shuttle.id)
      }
    });
  }

  setShuttlePartners(shuttleId: string, partners: ShuttlePartnerRecord[]): void {
    const state = this.shuttle();

    this.replaceSnapshot({
      partnersByShuttleId: {
        ...state.partnersByShuttleId,
        [shuttleId]: partners
      }
    });
  }

  setShuttleClients(shuttleId: string, clients: ShuttleClientRecord[]): void {
    const state = this.shuttle();

    this.replaceSnapshot({
      clientsByShuttleId: {
        ...state.clientsByShuttleId,
        [shuttleId]: clients
      }
    });
  }

  setShuttleNotes(shuttleId: string, notes: ShuttleNoteRecord[]): void {
    const state = this.shuttle();

    this.replaceSnapshot({
      notesByShuttleId: {
        ...state.notesByShuttleId,
        [shuttleId]: notes
      }
    });
  }

  patchShuttle(shuttleId: string, patch: Partial<ShuttleRecord>): void {
    const state = this.shuttle();
    const current = state.shuttlesById[shuttleId];

    if (!current) {
      return;
    }

    this.replaceSnapshot({
      shuttlesById: {
        ...state.shuttlesById,
        [shuttleId]: {
          ...current,
          ...patch
        }
      }
    });
  }

  selectLine(lineId: string | null): void {
    this.store.dispatch(new SelectShuttleLine(lineId));
  }

  selectShuttle(shuttleId: string | null): void {
    this.store.dispatch(new SelectShuttle(shuttleId));
  }

  clear(): void {
    this.store.dispatch(new ClearShuttleState());
  }
}

function indexBy<T>(records: T[], getKey: (record: T) => string): Record<string, T> {
  return records.reduce<Record<string, T>>((lookup, record) => {
    lookup[getKey(record)] = record;
    return lookup;
  }, {});
}
